// GENERATED by nuance-venue-platform/scripts/gen-permissions.ts — do not edit by hand.
// Source of truth: nuance-venue-platform/functions/src/auth/permissions.ts
//
// The console gates on the SAME rows the cloud enforces. A screen asks `can(role, 'scores.assign')`
// rather than re-deciding policy with a role array, so a hidden button and a refused call can never
// disagree — the console hiding a control the cloud would allow (or worse, showing one it refuses) was
// the drift this table exists to end. Regenerate with `npx tsx scripts/gen-permissions.ts`.

export type PermissionTier = 'operator' | 'manager' | 'owner' | 'staff';

export const PERMISSIONS = {
  'device.manage': 'owner',
  'effects.library.manage': 'staff',
  'effects.packages.manage': 'manager',
  'effects.run': 'operator',
  'members.manage': 'manager',
  'network.manage': 'manager',
  'notices.acknowledge': 'operator',
  'pairing.manage': 'manager',
  'records.write': 'operator',
  'releases.read': 'staff',
  'remotelive.policy': 'manager',
  'remotelive.session': 'manager',
  'rig.manage': 'manager',
  'rig.override': 'operator',
  'room.blackout': 'operator',
  'room.intensity_limit': 'manager',
  'room.manage': 'manager',
  'room.output_rate': 'manager',
  'room.takeover': 'operator',
  'room.view': 'operator',
  'scenes.manage': 'operator',
  'scores.assign': 'manager',
  'scores.delete': 'manager',
  'scores.play': 'operator',
  'scores.publish': 'manager',
  'stream.disconnect': 'operator',
  'updates.manage': 'manager',
  'venue.delete': 'owner',
  'venue.transfer_ownership': 'owner',
} as const;

export type PermissionName = keyof typeof PERMISSIONS;

/**
 * The rows that only READ venue state. They define the `support` role: platform support sees every
 * venue and changes nothing, so it is admitted to exactly these.
 */
export const READ_ONLY: ReadonlySet<string> = new Set<string>([
  'releases.read',
  'room.view',
]);

/** Venue roles rank operator < manager < owner; a super admin outranks every venue role. */
const RANK: Record<string, number> = { operator: 1, manager: 2, owner: 3, staff: 4 };

/**
 * May an actor holding `role` exercise `permission`? Mirrors the platform's assertPermission exactly.
 *
 * `'staff'` is the SUPER ADMIN (platform `staff: admin`): it bypasses every venue-scoped row and is the
 * only role a `staff`-tier row admits. `'support'` is the read-only half of the staff claim: admitted to
 * a read-only row on any venue, refused everything else. An unknown or absent role denies.
 */
export function can(role: string | null | undefined, permission: PermissionName): boolean {
  const tier = PERMISSIONS[permission];
  if (!role) return false;
  if (role === 'support') return READ_ONLY.has(permission);
  if (tier === 'staff') return role === 'staff';
  if (role === 'staff') return true;
  return (RANK[role] ?? 0) >= RANK[tier];
}
